// InterestCollectedByPeriodsReport.jsx
import * as React from "react";
import {
  Box,
  Button,
  Container,
  CssBaseline,
  Divider,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  alpha,
  Chip,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Avatar,
  IconButton,
  Tooltip,
  Fade,
  Zoom,
  Slide,
  Badge,
  CircularProgress
} from "@mui/material";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import GridOnOutlinedIcon from "@mui/icons-material/GridOnOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ScheduleIcon from "@mui/icons-material/Schedule";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import TimelineIcon from "@mui/icons-material/Timeline";
import AssessmentIcon from "@mui/icons-material/Assessment";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import { DataGrid } from "@mui/x-data-grid";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import AppNavbar from "../../../../components/AppNavbar";
import Header from "../../../../components/Header";
import SideMenu from "../../../../components/SideMenu";
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from '../../../dashboard/theme/customizations';
import AppTheme from "../../../../shared-theme/AppTheme";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { formatNumberWithCommas } from "../../../../utils/generalFunctions";
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const MetricCard = ({ label, value, icon, delay = 0 }) => (
  <Fade in timeout={800 + delay}>
  <Card sx={{ 
    p: 3, 
    minWidth: 280, 
    flex: 1,
      background: 'linear-gradient(135deg, #18586b 0%, #0f3a42 100%)',
    color: 'white',
    position: 'relative',
      overflow: 'hidden',
      cursor: 'pointer',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-8px) scale(1.02)',
        boxShadow: '0 20px 40px rgba(24, 88, 107, 0.3)',
        '& .metric-icon': {
          transform: 'scale(1.1) rotate(5deg)',
        },
        '& .metric-value': {
          transform: 'scale(1.05)',
        }
      }
  }}>
    <Box sx={{ position: 'relative', zIndex: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ opacity: 0.9 }}>
          {label}
        </Typography>
        {icon && (
            <Box className="metric-icon" sx={{ 
              p: 1.5, 
            borderRadius: '50%', 
            backgroundColor: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.1)'
          }}>
            {icon}
          </Box>
        )}
      </Box>
        <Typography className="metric-value" variant="h4" fontWeight={800} sx={{ 
          mb: 1,
          transition: 'all 0.3s ease',
          background: 'linear-gradient(45deg, #ffffff, #e0f2f1)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
        {value}
      </Typography>
        
      
        
      
    </Box>
      
      {/* Animated background elements */}
    <Box sx={{
      position: 'absolute',
      top: -20,
      right: -20,
      width: 80,
      height: 80,
      borderRadius: '50%',
      backgroundColor: 'rgba(255,255,255,0.1)',
        zIndex: 1,
        animation: 'float 6s ease-in-out infinite'
      }} />
      <Box sx={{
        position: 'absolute',
        bottom: -30,
        left: -30,
        width: 60,
        height: 60,
        borderRadius: '50%',
        backgroundColor: 'rgba(255,255,255,0.05)',
        zIndex: 1,
        animation: 'float 4s ease-in-out infinite reverse'
      }} />
      
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-10px) rotate(180deg); }
        }
      `}</style>
  </Card>
  </Fade>
);


// Beautiful Chart Components
const InterestTrendChart = ({ data }) => (
  <Card sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
      <Avatar sx={{ bgcolor: '#18586b', mr: 2 }}>
        <ShowChartIcon />
      </Avatar>
      <Typography variant="h6" fontWeight={700}>
        Tendencia de Intereses Cobrados
      </Typography>
    </Box>
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
        <XAxis dataKey="period" stroke="#666" />
        <YAxis stroke="#666" />
        <RechartsTooltip 
          contentStyle={{
            backgroundColor: '#18586b',
            border: 'none',
            borderRadius: 8,
            color: 'white'
          }}
        />
        <Line 
          type="monotone" 
          dataKey="interest" 
          stroke="#18586b" 
          strokeWidth={3}
          dot={{ fill: '#18586b', strokeWidth: 2, r: 6 }}
          activeDot={{ r: 8, stroke: '#18586b', strokeWidth: 2 }}
        />
      </LineChart>
    </ResponsiveContainer>
  </Card>
);

const PaymentBreakdownChart = ({ data }) => (
  <Card sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
      <Avatar sx={{ bgcolor: '#18586b', mr: 2 }}>
        <AssessmentIcon />
      </Avatar>
      <Typography variant="h6" fontWeight={700}>
        Desglose de Pagos por Tipo
    </Typography>
    </Box>
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
        <XAxis dataKey="period" stroke="#666" />
        <YAxis stroke="#666" />
        <RechartsTooltip 
          contentStyle={{
            backgroundColor: '#18586b',
            border: 'none',
            borderRadius: 8,
            color: 'white'
          }}
        />
        <Bar dataKey="interest" fill="#18586b" radius={[4, 4, 0, 0]} />
        <Bar dataKey="capital" fill="#2a7a8a" radius={[4, 4, 0, 0]} />
        <Bar dataKey="mora" fill="#3c9bb8" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  </Card>
);

const BranchPerformanceChart = ({ data }) => (
  <Card sx={{ p: 3, background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
      <Avatar sx={{ bgcolor: '#18586b', mr: 2 }}>
        <TimelineIcon />
      </Avatar>
      <Typography variant="h6" fontWeight={700}>
        Rendimiento por Sucursal
    </Typography>
    </Box>
    <ResponsiveContainer width="100%" height={250}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
          outerRadius={80}
          fill="#8884d8"
          dataKey="value"
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={['#18586b', '#2a7a8a', '#3c9bb8'][index % 3]} />
          ))}
        </Pie>
        <RechartsTooltip 
          contentStyle={{
            backgroundColor: '#18586b',
            border: 'none',
            borderRadius: 8,
            color: 'white'
          }}
        />
      </PieChart>
    </ResponsiveContainer>
  </Card>
);

export default function InteresesCobradosPorPeriodosReport(props) {

  // ---- Filters (controlled state) ----
  const [branch, setBranch] = React.useState("");
  const [product, setProduct] = React.useState("");
  const [dateFrom, setDateFrom] = React.useState(null);
  const [dateTo, setDateTo] = React.useState(null);

  // ---- Data state ----
  const [rows, setRows] = React.useState([]);
  const [filteredRows, setFilteredRows] = React.useState([]);
  const [isLoadingData, setIsLoadingData] = React.useState(false);

  // ---- Extract unique values for filter options ----
  const filterOptions = React.useMemo(() => {
    if (rows.length === 0) {
      return {
        branches: [],
        products: []
      };
    }

    // Extract unique branches
    const branches = [...new Set(rows.map(row => row.branch).filter(Boolean))].sort();

    // Extract unique products
    const products = [...new Set(rows.map(row => row.product).filter(Boolean))].sort();

    return {
      branches,
      products
    };
  }, [rows]);

  const currency = (n) => {
    if (n === undefined || n === null || isNaN(n)) return 'RD$0';
    return `RD$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  };

  const columns = [
    { field: "loanId", headerName: "ID Préstamo", flex: 1, minWidth: 150 },
    { field: "client", headerName: "Cliente", flex: 1, minWidth: 200 },
    { field: "branch", headerName: "Sucursal", flex: 1, minWidth: 150 },
    {
      field: "interestCollected",
      headerName: "Intereses",
      minWidth: 160,
      renderCell: (params) => {
        return <Typography variant="body2" mt={2}>{formatNumberWithCommas(params.row.interestCollected.toFixed(2))}</Typography>
      }
    },
    {
      field: "capitalCollected",
      headerName: "Capital",
      minWidth: 160,
      renderCell: (params) => {
        return <Typography variant="body2" mt={2}>{formatNumberWithCommas(params.row.capitalCollected.toFixed(2))}</Typography>
      }
    },
    {
      field: "moraCollected",
      headerName: "Mora",
      minWidth: 160,
      renderCell: (params) => {
        return <Typography variant="body2" mt={2}>{formatNumberWithCommas(params.row.moraCollected.toFixed(2))}</Typography>
      }
    },
    {
      field: "totalCollected",
      headerName: "Monto Total",
      minWidth: 160,
      renderCell: (params) => {
        return <Typography variant="body2" mt={2}>{formatNumberWithCommas(params.row.totalCollected.toFixed(2))}</Typography>
      }
    },

  ];

  // ---- Calculate totals and metrics ----
  const totals = filteredRows.reduce(
    (acc, r) => {
      acc.totalInterest += parseFloat(r.interestCollected) || 0;
      acc.totalCapital += parseFloat(r.capitalCollected) || 0;
      acc.totalMora += parseFloat(r.moraCollected) || 0;
      acc.totalCollected += parseFloat(r.totalCollected) || 0;
      acc.totalTransactions += 1;
      acc.averageInterest += parseFloat(r.interestCollected) || 0;
      return acc;
    },
    { 
      totalInterest: 0, 
      totalCapital: 0, 
      totalMora: 0, 
      totalCollected: 0, 
      totalTransactions: 0, 
      averageInterest: 0,
      totalCompletedLoans: 0
    }
  );

  // Calculate derived values
  totals.averageInterest = totals.totalTransactions > 0 ? totals.totalInterest / totals.totalTransactions : 0;
  totals.totalCompletedLoans = filteredRows.filter(r => r.status === 'cobrado' || r.status === 'completado').length;

  // ---- Period breakdown data ----
  const periodData = [
    { period: "Enero 2024", amount: currency(4500), percentage: 35, color: "#18586b" },
    { period: "Febrero 2024", amount: currency(1533), percentage: 12, color: "#2a7a8a" },
    { period: "Marzo 2024", amount: currency(2166), percentage: 17, color: "#3c9bb8" },
    { period: "Abril 2024", amount: currency(3200), percentage: 25, color: "#0f3a42" },
    { period: "Mayo 2024", amount: currency(1500), percentage: 11, color: "#4eb3c7" },
  ];

  // ---- Chart data (generated from filtered data) ----
  const trendData = React.useMemo(() => {
    if (filteredRows.length === 0) return [];
    
    // Group by month and sum values
    const monthlyData = filteredRows.reduce((acc, row) => {
      const date = new Date(row.paymentDate);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleDateString('es-ES', { month: 'short' });
      
      if (!acc[monthKey]) {
        acc[monthKey] = {
          period: monthName,
          interest: 0,
          capital: 0,
          mora: 0
        };
      }
      
      acc[monthKey].interest += parseFloat(row.interestCollected) || 0;
      acc[monthKey].capital += parseFloat(row.capitalCollected) || 0;
      acc[monthKey].mora += parseFloat(row.moraCollected) || 0;
      
      return acc;
    }, {});
    
    return Object.values(monthlyData).sort((a, b) => a.period.localeCompare(b.period));
  }, [filteredRows]);

  const branchData = React.useMemo(() => {
    if (filteredRows.length === 0) return [];
    
    // Group by branch and sum interest collected
    const branchTotals = filteredRows.reduce((acc, row) => {
      const branch = row.branch || 'Sin Sucursal';
      acc[branch] = (acc[branch] || 0) + (parseFloat(row.interestCollected) || 0);
      return acc;
    }, {});
    
    return Object.entries(branchTotals).map(([name, value]) => ({
      name,
      value
    }));
  }, [filteredRows]);

  const [isLoading, setIsLoading] = React.useState(false);
  const [showCharts, setShowCharts] = React.useState(true);
  const navigate = useNavigate(); 

  // ---- Fetch data from API ----
  const fetchData = async () => {
    setIsLoadingData(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/prestamos/reporte/interesescobradosperiodos?idcompany=${localStorage.getItem('idcompany')}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!Array.isArray(data)) {
        console.error('API response is not an array:', data);
        return;
      }

      // Backend devuelve paymentDate como "dd-MM-yyyy" (date_format en SQL).
      // dayjs y new Date no parsean ese formato: lo invierten o lo descartan.
      // Normalizamos a ISO "yyyy-MM-dd" para que filtros y charts funcionen.
      const toIsoDate = (raw) => {
        if (!raw) return new Date().toISOString().split('T')[0];
        if (typeof raw === 'string') {
          const m = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
          if (m) return `${m[3]}-${m[2]}-${m[1]}`;
        }
        return raw;
      };

      // Transform API data to match frontend expectations
      const transformedData = data.map((item, index) => ({
        id: index + 1, // Generate sequential ID
        loanId: item.loan_id || `PREST-${item.id || index}`,
        client: item.client || 'Cliente no especificado',
        product: item.product || 'Producto no especificado',
        branch: item.branch || 'Sucursal no especificada',
        totalCollected: parseFloat(item.totalCollected) || 0,
        interestCollected: parseFloat(item.interestCollected) || 0,
        capitalCollected: parseFloat(item.capitalCollected) || 0,
        moraCollected: parseFloat(item.moraCollected) || 0,
        period: item.period || item.paymentDate || 'Período no especificado',
        paymentDate: toIsoDate(item.paymentDate || item.period),
        status: item.status || 'cobrado'
      }));

      setRows(transformedData);
      setFilteredRows(transformedData); // Initialize filtered data
      console.log('Data loaded successfully:', transformedData);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      alert(`Error al cargar los datos: ${error.message}`);
    } finally {
      setIsLoadingData(false);
    }
  };

  // ---- Filter data based on selected filters ----
  const handleGenerate = () => {
    setIsLoading(true);
    
    // Simulate processing time
    setTimeout(() => {
      let filtered = [...rows];

      // Filter by branch
      if (branch) {
        filtered = filtered.filter(row => row.branch === branch);
      }

      // Filter by product
      if (product) {
        filtered = filtered.filter(row => row.product === product);
      }

      // Filter by date range
      if (dateFrom) {
        filtered = filtered.filter(row => {
          const rowDate = dayjs(row.paymentDate);
          return rowDate.isAfter(dateFrom) || rowDate.isSame(dateFrom, 'day');
        });
      }
      if (dateTo) {
        filtered = filtered.filter(row => {
          const rowDate = dayjs(row.paymentDate);
          return rowDate.isBefore(dateTo) || rowDate.isSame(dateTo, 'day');
        });
      }

      setFilteredRows(filtered);
      setIsLoading(false);
      console.log('Data filtered successfully:', filtered);
    }, 500);
  };

  const handleExportPDF = () => {
    if (filteredRows.length === 0) {
      alert('No hay datos para exportar. Por favor aplique filtros primero.');
      return;
    }

    try {
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      let yPosition = 20;

      const companyName = localStorage.getItem('nombre_compania') || 'Empresa';
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Intereses Cobrados por Períodos', 14, yPosition);

      yPosition += 8;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Empresa: ${companyName}`, 14, yPosition);

      yPosition += 5;
      const dateRange = `Período: ${dateFrom ? dayjs(dateFrom).format('DD/MM/YYYY') : 'N/A'} - ${dateTo ? dayjs(dateTo).format('DD/MM/YYYY') : 'N/A'}`;
      doc.text(dateRange, 14, yPosition);

      yPosition += 5;
      doc.text(`Generado: ${dayjs().format('DD/MM/YYYY HH:mm:ss')}`, 14, yPosition);

      yPosition += 8;

      const activeFilters = [];
      if (branch) activeFilters.push(`Sucursal: ${branch}`);
      if (product) activeFilters.push(`Producto: ${product}`);

      if (activeFilters.length > 0) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('Filtros Aplicados:', 14, yPosition);
        yPosition += 5;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        activeFilters.forEach(filter => {
          doc.text(`• ${filter}`, 18, yPosition);
          yPosition += 4;
        });
        yPosition += 3;
      }

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Resumen', 14, yPosition);
      yPosition += 6;

      const summaryData = [
        ['Total Intereses Cobrados', currency(totals.totalInterest)],
        ['Total Capital Cobrado', currency(totals.totalCapital)],
        ['Total Mora Cobrada', currency(totals.totalMora)],
        ['Total General Cobrado', currency(totals.totalCollected)],
        ['Total Transacciones', totals.totalTransactions.toString()]
      ];

      autoTable(doc, {
        startY: yPosition,
        head: [['Concepto', 'Valor']],
        body: summaryData,
        theme: 'grid',
        headStyles: { fillColor: [24, 88, 107], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 9 },
        columnStyles: {
          0: { cellWidth: 70, fontStyle: 'bold' },
          1: { cellWidth: 50, halign: 'right' }
        },
        margin: { left: 14, right: 14 }
      });

      yPosition = doc.lastAutoTable.finalY + 10;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Detalle de Pagos', 14, yPosition);
      yPosition += 6;

      const tableData = filteredRows.map(row => [
        row.loanId || '',
        row.client || '',
        row.branch || '',
        row.product || '',
        row.paymentDate || '',
        formatNumberWithCommas((parseFloat(row.capitalCollected) || 0).toFixed(2)),
        formatNumberWithCommas((parseFloat(row.interestCollected) || 0).toFixed(2)),
        formatNumberWithCommas((parseFloat(row.moraCollected) || 0).toFixed(2)),
        formatNumberWithCommas((parseFloat(row.totalCollected) || 0).toFixed(2))
      ]);

      autoTable(doc, {
        startY: yPosition,
        head: [['ID Préstamo', 'Cliente', 'Sucursal', 'Producto', 'Fecha', 'Capital', 'Interés', 'Mora', 'Total']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [24, 88, 107], textColor: 255, fontStyle: 'bold', fontSize: 8 },
        bodyStyles: { fontSize: 7 },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        columnStyles: {
          0: { cellWidth: 28 },
          1: { cellWidth: 38 },
          2: { cellWidth: 28 },
          3: { cellWidth: 30 },
          4: { cellWidth: 22 },
          5: { cellWidth: 25, halign: 'right' },
          6: { cellWidth: 25, halign: 'right' },
          7: { cellWidth: 22, halign: 'right' },
          8: { cellWidth: 25, halign: 'right' }
        },
        margin: { left: 14, right: 14 },
        styles: { overflow: 'linebreak', cellPadding: 2 }
      });

      const pageCount = doc.internal.pages.length - 1;
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.text(
          `Página ${i} de ${pageCount}`,
          doc.internal.pageSize.width / 2,
          doc.internal.pageSize.height - 10,
          { align: 'center' }
        );
      }

      const pdfBlob = doc.output('blob');
      const url = URL.createObjectURL(pdfBlob);
      window.open(url, '_blank');

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(`No se pudo generar el PDF: ${error.message}`);
    }
  };
  
  const handleExportExcel = () => {
    console.log("Export Excel");
  };
  
  const handlePrint = () => window.print();

  const handleRefresh = async () => {
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsLoading(false);
  };

  const handleToggleCharts = () => {
    setShowCharts(!showCharts);
  };

  const handleClearFilters = () => {
    setBranch("");
    setProduct("");
    setDateFrom(null);
    setDateTo(null);
    setFilteredRows(rows); // Reset to show all data
  };

  // ---- Filter content ----
  const filterContent = (
    <Box>
      {/* Filter Controls Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>

        <Grid item xs={12} sm={6} md={3}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left', mb: 1 }}>
              Sucursal
            </Typography>
            <FormControl fullWidth>
              <Select
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                displayEmpty
                sx={{ backgroundColor: 'white' }}
              >
                <MenuItem value="">Todas las Sucursales</MenuItem>
                {filterOptions.branches.map((branchOption) => (
                  <MenuItem key={branchOption} value={branchOption}>
                    {branchOption}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left', mb: 1 }}>
              Producto Financiero
            </Typography>
            <FormControl fullWidth>
              <Select
                value={product}
                onChange={(e) => setProduct(e.target.value)}
                displayEmpty
                sx={{ backgroundColor: 'white' }}
              >
                <MenuItem value="">Todos los Productos</MenuItem>
                {filterOptions.products.map((productOption) => (
                  <MenuItem key={productOption} value={productOption}>
                    {productOption}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left', mb: 1 }}>
              Fecha Desde
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={dateFrom}
                format="DD/MM/YYYY"
                onChange={(newValue) => setDateFrom(newValue)}
                label="Seleccionar fecha"
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { backgroundColor: 'white' }
                  }
                }}
              />
            </LocalizationProvider>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left', mb: 1 }}>
              Fecha Hasta
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={dateTo}
                format="DD/MM/YYYY"
                onChange={(newValue) => setDateTo(newValue)}
                label="Seleccionar fecha"
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { backgroundColor: 'white' }
                  }
                }}
              />
            </LocalizationProvider>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={12} display={"flex"} flexDirection={"row"} gap={2}>
          <Button 
            variant="contained" 
            onClick={handleGenerate} 
            color="primarybutton" 
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <FilterListIcon />}
            sx={{
              fontWeight: 600, 
              minWidth: 120, 
              mt: 3,
              background: 'linear-gradient(45deg, #18586b, #2a7a8a)',
              '&:hover': {
                background: 'linear-gradient(45deg, #0f3a42, #18586b)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(24, 88, 107, 0.3)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {isLoading ? 'Filtrando...' : 'Aplicar Filtros'}
          </Button>

          <Button 
            variant="outlined" 
            onClick={handleClearFilters} 
            disabled={isLoading}
            sx={{
              fontWeight: 600, 
              minWidth: 120, 
              mt: 3,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Limpiar Filtros
          </Button>

        </Grid>

      </Grid>

      {/* Export Options Row */}
      <Stack 
        direction={{ xs: "column", sm: "row" }} 
        spacing={2} 
        alignItems="center"
        sx={{ 
          borderTop: 1, 
          borderColor: 'divider', 
          pt: 2,
          justifyContent: 'space-between'
        }}
      >
        <Stack direction="row" spacing={1}>
          <Tooltip title="Exportar a PDF">
            <Button 
              onClick={handleExportPDF} 
              startIcon={<PictureAsPdfOutlinedIcon />} 
              color="inherit" 
              size="small"
              sx={{
                '&:hover': {
                  backgroundColor: '#f44336',
                  color: 'white',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              PDF
          </Button>
          </Tooltip>
          <Tooltip title="Exportar a Excel">
            <Button 
              onClick={handleExportExcel} 
              startIcon={<GridOnOutlinedIcon />} 
              color="inherit" 
              size="small"
              sx={{
                '&:hover': {
                  backgroundColor: '#4caf50',
                  color: 'white',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Excel
          </Button>
          </Tooltip>
          <Tooltip title="Imprimir Reporte">
            <Button 
              onClick={handlePrint} 
              startIcon={<PrintOutlinedIcon />} 
              color="inherit" 
              size="small"
              sx={{
                '&:hover': {
                  backgroundColor: '#ff9800',
                  color: 'white',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Imprimir
          </Button>
          </Tooltip>
        </Stack>
        
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Actualizar Datos">
            <IconButton 
              onClick={handleRefresh} 
              disabled={isLoading}
              sx={{
                '&:hover': {
                  backgroundColor: '#18586b',
                  color: 'white',
                  transform: 'rotate(180deg)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={showCharts ? "Ocultar Gráficos" : "Mostrar Gráficos"}>
            <IconButton 
              onClick={handleToggleCharts}
              sx={{
                '&:hover': {
                  backgroundColor: '#18586b',
                  color: 'white'
                },
                transition: 'all 0.3s ease'
              }}
            >
              <ShowChartIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Box>
  );

  React.useEffect(() => {
    fetchData();
  }, []);

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        {/* Main content */}
        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : alpha(theme.palette.background.default, 1),
            overflow: 'auto',
          })}
        >
          <Stack
            spacing={2}
            sx={{
              alignItems: 'center',
              mx: 3,
              pb: 5,
              mt: { xs: 8, md: 0 },
            }}
          >
            <Header parent={"Gestión Comercial"} child={"Reportes"} />
            <Container maxWidth={false} sx={{ px: 5, py: 5 }}>
              <Box sx={{ minHeight: '100vh', backgroundColor: '#fff', fontFamily: 'Inter, "Noto Sans", sans-serif' }}>
                <Box maxWidth="lg" mx="auto" py={4}>
                  <Box display="flex" px={2} mb={2}>
                    <Button variant="outlined" startIcon={<ArrowBack/>} onClick={()=>{navigate(-1)}}>Atras</Button>
                    <Box display={"flex"} flexDirection={"column"} gap={2} ml={5}>
                        <Typography variant="h4" fontWeight={700} textAlign={"left"}>Intereses Cobrados por Períodos</Typography>
                        <Typography variant="body1" color="text.secondary" textAlign={"left"} sx={{mt:-1}}>Registro detallado de intereses cobrados por periodos</Typography>
                    </Box>
                  </Box>

                  {/* Filters Top Bar */}
                  <Paper sx={{ p: 3, mb: 3 }}>
                    {filterContent}
                  </Paper>

                  {/* Metric Cards */}
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    useFlexGap
                    sx={{ mb: 4, flexWrap: "wrap" }}
                  >
                    <MetricCard 
                      label="Total Intereses Cobrados" 
                      value={currency(totals.totalInterest)} 
                      icon={<AccountBalanceIcon />}
                      delay={0}
                    />
                    <MetricCard 
                      label="Total Capital Cobrado" 
                      value={currency(totals.totalCapital)} 
                      icon={<TrendingUpIcon />}
                      delay={200}
                    />
                    <MetricCard 
                      label="Total Mora Cobrada" 
                      value={currency(totals.totalMora)} 
                      icon={<ScheduleIcon />}
                      delay={400}
                    />
                  </Stack>

                

                  {/* Beautiful Charts Section */}
                  {showCharts && (
                  <Slide direction="up" in={showCharts} timeout={800}>
                  <Box sx={{ mb: 4 }}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} lg={8}>
                          <InterestTrendChart data={trendData} />
                        </Grid>
                        <Grid item xs={12} lg={4}>
                          <BranchPerformanceChart data={branchData} />
                        </Grid>
                        <Grid item xs={12}>
                          <PaymentBreakdownChart data={trendData} />
                        </Grid>
                    </Grid>
                  </Box>
                  </Slide>
                  )}


                  {/* Data Grid */}
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    {filteredRows.length === 0 && !isLoadingData ? (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        py: 8,
                        textAlign: 'center'
                      }}>
                        <Avatar sx={{ bgcolor: '#18586b', mb: 2, width: 64, height: 64 }}>
                          <AssessmentIcon sx={{ fontSize: 32 }} />
                        </Avatar>
                        <Typography variant="h6" fontWeight={600} color="text.primary" mb={1}>
                          No hay datos disponibles
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={3}>
                          No se encontraron registros de intereses cobrados para los filtros seleccionados.
                        </Typography>
                        <Button 
                          variant="contained" 
                          onClick={handleGenerate}
                          color="primarybutton"
                          startIcon={<RefreshIcon />}
                        >
                          Aplicar Filtros
                        </Button>
                      </Box>
                    ) : (
                    <DataGrid
                      autoHeight
                        rows={filteredRows}
                      columns={columns}
                      disableRowSelectionOnClick
                        loading={isLoadingData}
                      pageSizeOptions={[5, 10, 25]}
                      initialState={{
                        pagination: { paginationModel: { pageSize: 10 } },
                      }}
                      sx={{
                        "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700 },
                      }}
                    />
                    )}
                  </Paper>

                  {/* Totals footer */}
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={3}
                    sx={{ mt: 2, color: "text.secondary" }}
                  >
                    <Typography variant="body2">
                      <strong>Total Intereses Cobrados:</strong> {currency(totals.totalInterest)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total Mora Cobrada:</strong> {currency(totals.totalMora)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total Capital Cobrado:</strong> {currency(totals.totalCapital)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total Préstamos Completados:</strong> {totals.totalCompletedLoans}
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Container>
          </Stack>
        </Box>
      </Box>
    </AppTheme>
  );
}
